import dayjs from 'dayjs';
import type { CrmDealListItem, CrmStage } from '@ip/shared-types';
import { t } from '@/i18n';
import type { Tone } from './tones';

/**
 * Pure helpers for the deal board — deliberately free of React so the
 * thresholds and the money/relative-day rules can be reviewed line by line
 * (the frontend has no test runner). The `*Label` helpers at the bottom are
 * the ONE place that picks singular vs plural i18n keys ("1 dzień" vs
 * "{days} dni"), so the card, the stepper and the sheet badge agree.
 */

/** Days without any activity after which a deal is "stale" (amber). */
export const STALE_DAYS = 7;
/** Days without any activity after which a deal is "critical" (destructive). */
export const CRITICAL_DAYS = 14;

export type DealHealth = 'fresh' | 'stale' | 'critical' | 'closed';

/** The pipeline's stages, sorted and indexed once per load. */
export interface StageIndex {
  /** Active, non-terminal stages by sortOrder — the board's columns and the `]`/`[` order. */
  open: CrmStage[];
  won?: CrmStage;
  lost?: CrmStage;
  byId: ReadonlyMap<string, CrmStage>;
}

export function indexStages(stages: ReadonlyArray<CrmStage>): StageIndex {
  return {
    open: stages.filter((stage) => stage.active && !isClosedStage(stage)).sort((a, b) => a.sortOrder - b.sortOrder),
    won: stages.find((stage) => stage.isWon),
    lost: stages.find((stage) => stage.isLost),
    byId: new Map(stages.map((stage) => [stage.id, stage])),
  };
}

/** `12.03.2026` / `12.03.2026 14:05` — the one date format the deal UI shows. */
export function fmtDate(date: string | Date, withTime = false): string {
  return dayjs(date).format(withTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY');
}

export interface CurrencyBucket {
  sum: number;
  count: number;
}

/** Sums valueNet per currency; deals without a value are skipped entirely. */
export function sumByCurrency(deals: ReadonlyArray<CrmDealListItem>): Map<string, CurrencyBucket> {
  const out = new Map<string, CurrencyBucket>();
  for (const deal of deals) {
    if (deal.valueNet === null) continue;
    const bucket = out.get(deal.currency) ?? { sum: 0, count: 0 };
    bucket.sum += deal.valueNet;
    bucket.count += 1;
    out.set(deal.currency, bucket);
  }
  return out;
}

/** valueNet × probability; null when the deal has no value. */
export function weightedValue(deal: Pick<CrmDealListItem, 'valueNet' | 'probability'>): number | null {
  if (deal.valueNet === null) return null;
  return (deal.valueNet * (deal.probability ?? 0)) / 100;
}

/** Weighted PLN sum — the only currency the pipeline forecast counts (no FX). */
export function weightedSumPln(deals: ReadonlyArray<CrmDealListItem>): number {
  let total = 0;
  for (const deal of deals) {
    if (deal.currency !== 'PLN') continue;
    total += weightedValue(deal) ?? 0;
  }
  return total;
}

/** Whole calendar days from `from` to `to` (local time), negative when `to` is earlier. */
export function daysBetween(from: string | Date, to: string | Date): number {
  return dayjs(to).startOf('day').diff(dayjs(from).startOf('day'), 'day');
}

function isClosedStage(stage: Pick<CrmStage, 'isWon' | 'isLost'> | undefined): boolean {
  return !!stage && (stage.isWon || stage.isLost);
}

export function isClosedDeal(
  deal: Pick<CrmDealListItem, 'closedAt'>,
  stage?: Pick<CrmStage, 'isWon' | 'isLost'>,
): boolean {
  return deal.closedAt !== null || isClosedStage(stage);
}

/**
 * Days since the newest activity. A deal without any activity counts from the
 * moment it entered its stage (= createdAt for a never-moved deal), so a
 * freshly converted lead is "fresh", not instantly critical.
 */
export function daysSinceActivity(
  deal: Pick<CrmDealListItem, 'lastActivityAt' | 'stageEnteredAt'>,
  now: Date | string,
): number {
  return Math.max(0, daysBetween(deal.lastActivityAt ?? deal.stageEnteredAt, now));
}

export function daysInStage(deal: Pick<CrmDealListItem, 'stageEnteredAt'>, now: Date | string): number {
  return Math.max(0, daysBetween(deal.stageEnteredAt, now));
}

/** Expected close is strictly before today. */
export function isPastDue(
  deal: Pick<CrmDealListItem, 'expectedCloseAt'>,
  now: Date | string,
): boolean {
  return deal.expectedCloseAt !== null && daysBetween(deal.expectedCloseAt, now) > 0;
}

/**
 * fresh → stale (≥7 d without activity) → critical (≥14 d without activity OR
 * expected close in the past). Closed deals carry no health at all.
 */
export function dealHealth(
  deal: CrmDealListItem,
  now: Date | string,
  stage?: Pick<CrmStage, 'isWon' | 'isLost'>,
): DealHealth {
  if (isClosedDeal(deal, stage)) return 'closed';
  if (isPastDue(deal, now)) return 'critical';
  const idle = daysSinceActivity(deal, now);
  if (idle >= CRITICAL_DAYS) return 'critical';
  if (idle >= STALE_DAYS) return 'stale';
  return 'fresh';
}

/** Colour of a "days in stage" counter: critical → destructive, stale → amber, else muted. */
export function daysTone(days: number): Tone {
  if (days >= CRITICAL_DAYS) return 'destructive';
  if (days >= STALE_DAYS) return 'amber';
  return 'muted';
}

export function healthTone(health: DealHealth): Tone {
  switch (health) {
    case 'fresh':
      return 'emerald';
    case 'stale':
      return 'amber';
    case 'critical':
      return 'destructive';
    case 'closed':
      return 'muted';
  }
}

export type RelativeDayKind = 'today' | 'tomorrow' | 'yesterday' | 'inDays' | 'daysAgo';

export interface RelativeDay {
  kind: RelativeDayKind;
  /** Absolute day distance (0 for today). */
  days: number;
}

/** Calendar-day distance in the vocabulary the i18n keys `crm.deals.relative.*` use. */
export function relativeDay(date: string | Date, now: Date | string): RelativeDay {
  const diff = daysBetween(now, date);
  if (diff === 0) return { kind: 'today', days: 0 };
  if (diff === 1) return { kind: 'tomorrow', days: 1 };
  if (diff === -1) return { kind: 'yesterday', days: 1 };
  return diff > 0 ? { kind: 'inDays', days: diff } : { kind: 'daysAgo', days: -diff };
}

/** `1 dzień w etapie` / `{days} dni w etapie` — the card's clock line. */
export function inStageLabel(days: number): string {
  return t(days === 1 ? 'crm.deals.card.inStageOne' : 'crm.deals.card.inStage', { days });
}

/** Past-due chip text: `1 dzień po terminie` / `{days} dni po terminie`. */
export function overdueLabel(days: number): string {
  return t(days === 1 ? 'crm.deals.relative.overdueOne' : 'crm.deals.relative.overdueDays', { days });
}

/** `dziś` / `wczoraj` / `{days} dni temu` for a past timestamp (activity dates). */
export function relativeAgoLabel(date: string | Date, now: Date | string): string {
  const rel = relativeDay(date, now);
  if (rel.kind === 'today') return t('crm.deals.relative.today');
  if (rel.kind === 'yesterday') return t('crm.deals.relative.yesterday');
  return t('crm.deals.relative.daysAgo', { days: rel.days });
}

/**
 * Text + tone of the sheet's health badge (`crm.deals.health.*`), null for a
 * closed deal. This is the ONE place that picks singular vs plural wording
 * and pairs it with the colour, so the badge cannot say "stale" in green.
 */
export function healthLabel(
  deal: CrmDealListItem,
  now: Date | string,
  stage?: Pick<CrmStage, 'isWon' | 'isLost'>,
): { label: string; tone: Tone } | null {
  const health = dealHealth(deal, now, stage);
  if (health === 'closed') return null;
  if (isPastDue(deal, now) && deal.expectedCloseAt) {
    const days = daysBetween(deal.expectedCloseAt, now);
    return {
      label: t(days === 1 ? 'crm.deals.health.overdueOne' : 'crm.deals.health.overdue', { days }),
      tone: 'destructive',
    };
  }
  if (health === 'fresh') {
    return {
      label: deal.lastActivityAt
        ? t('crm.deals.health.fresh', { ago: relativeAgoLabel(deal.lastActivityAt, now) })
        : t('crm.deals.health.freshNoActivity'),
      tone: 'emerald',
    };
  }
  const idle = daysSinceActivity(deal, now);
  return {
    label: t(idle === 1 ? 'crm.deals.health.staleOne' : 'crm.deals.health.stale', { days: idle }),
    tone: healthTone(health),
  };
}
